import React, { useCallback, useEffect, useMemo, useState } from "react";
import DataTable, { SampleStatusCell } from "../components/common/DataTable";
import ConfirmModal from "../components/common/ConfirmModal";
import { useAdminApi } from "../hooks/useAdminApi";
import { PayoutSummary } from "../types/admin";
import { toast } from "react-toastify";

interface PayoutRow {
	id: number;
	external_id?: string;
	organizer: string;
	amount: number;
	status: "pending" | "approved" | "paid" | "rejected";
	requested_at?: string | null;
}

type PayoutAction = "approve" | "reject" | "paid";

const PayoutsPage: React.FC = () => {
	const api = useAdminApi();
	const [filters, setFilters] = useState({ q: "", status: "" });
	const [rows, setRows] = useState<PayoutRow[]>([]);
	const [page, setPage] = useState(1);
	const [pageSize, setPageSize] = useState(20);
	const [total, setTotal] = useState(0);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const [target, setTarget] = useState<PayoutRow | null>(null);
	const [action, setAction] = useState<PayoutAction | null>(null);
	const [modalOpen, setModalOpen] = useState(false);
	const [actionLoading, setActionLoading] = useState(false);

	const mapPayout = useCallback(
		(p: PayoutSummary): PayoutRow => ({
			id: p.id,
			external_id: p.external_id,
			organizer: p.organizer?.organizer_name ?? "-",
			amount: Number(p.amount ?? 0),
			status: p.status,
			requested_at: p.requested_at,
		}),
		[]
	);

	const fetchPayouts = useCallback(
		async (pageOverride?: number) => {
			setLoading(true);
			setError(null);
			try {
				const res = await api.listPayouts({
					status: filters.status || undefined,
					external_id: filters.q || undefined,
					page: pageOverride ?? page,
					per_page: pageSize,
				});
				const mapped = res.data.map(mapPayout);
				setRows(mapped);
				setTotal(res.total);
				setPageSize(res.per_page);
			} catch (err) {
				console.warn("Failed to load payouts", err);
				setError("入金申請一覧の取得に失敗しました");
			} finally {
				setLoading(false);
			}
		},
		[api, filters, page, pageSize, mapPayout]
	);

	useEffect(() => {
		fetchPayouts();
	}, [fetchPayouts]);

	const columns = useMemo(
		() => [
			{ key: "external_id", header: "申請ID" },
			{ key: "organizer", header: "オーガナイザー" },
			{
				key: "amount",
				header: "金額",
				align: "right" as const,
				render: (row: PayoutRow): React.ReactNode =>
					row.amount ? `¥${row.amount.toLocaleString()}` : "-",
			},
			{
				key: "status",
				header: "状態",
				render: (row: PayoutRow) => <SampleStatusCell status={row.status} />,
			},
			{ key: "requested_at", header: "申請日" },
			{
				key: "actions",
				header: "操作",
				render: (row: PayoutRow) => (
					<div className="flex flex-wrap gap-2 text-xs font-semibold">
						<button
							className="text-primary disabled:opacity-40"
							disabled={row.status !== "pending"}
							onClick={() => {
								setTarget(row);
								setAction("approve");
								setModalOpen(true);
							}}
						>
							承認
						</button>
						<button
							className="text-rose-600 disabled:opacity-40"
							disabled={row.status !== "pending"}
							onClick={() => {
								setTarget(row);
								setAction("reject");
								setModalOpen(true);
							}}
						>
							却下
						</button>
						<button
							className="text-indigo-600 disabled:opacity-40"
							disabled={row.status !== "approved"}
							onClick={() => {
								setTarget(row);
								setAction("paid");
								setModalOpen(true);
							}}
						>
							paid マーク
						</button>
					</div>
				),
			},
		],
		[]
	);

	const handleConfirm = async (reason?: string) => {
		if (!target || !action) return;
		setActionLoading(true);
		try {
			if (action === "approve") {
				await api.approvePayout(target.id, reason);
			} else if (action === "reject") {
				await api.rejectPayout(target.id, reason || "理由未入力");
			} else if (action === "paid") {
				await api.markPayoutPaid(target.id);
			}
			toast.success("入金申請ステータスを更新しました");
			setModalOpen(false);
			fetchPayouts();
		} catch (err) {
			console.error("Failed to update payout", err);
			toast.error("ステータス更新に失敗しました");
		} finally {
			setActionLoading(false);
			setAction(null);
			setTarget(null);
		}
	};

	const handleSearch = () => {
		setPage(1);
		fetchPayouts(1);
	};

	const showReason = action !== "paid";
	const requireReason = action === "reject";

	return (
		<div className="space-y-4">
			<div className="flex items-center justify-between">
				<div>
					<h1 className="text-xl font-semibold text-ink">Payouts</h1>
					<p className="text-sm text-muted">
						承認 / 却下 / paid マーク + 履歴テーブル
					</p>
				</div>
				<button className="rounded-lg border border-surface-border px-3 sm:px-4 py-2 text-sm font-semibold text-primary">
					CSV Export
				</button>
			</div>

			<div className="rounded-2xl border border-surface-border bg-white p-4 shadow-card">
				<div className="grid gap-3 md:grid-cols-4">
					<input
						className="input"
						placeholder="申請ID (external_id)"
						value={filters.q}
						onChange={(e) => setFilters({ ...filters, q: e.target.value })}
					/>
					<select
						className="input"
						value={filters.status}
						onChange={(e) => setFilters({ ...filters, status: e.target.value })}
					>
						<option value="">状態</option>
						<option value="pending">pending</option>
						<option value="approved">approved</option>
						<option value="paid">paid</option>
						<option value="rejected">rejected</option>
					</select>
					<div className="md:col-span-2 flex items-center gap-2">
						<button
							className="rounded-xl bg-primary px-3 sm:px-4 py-2 text-sm font-semibold text-white"
							onClick={handleSearch}
						>
							検索
						</button>
						<span className="text-xs text-muted">
							status / external_id で検索
						</span>
					</div>
				</div>

				<div className="mt-4">
					<DataTable
						data={rows}
						columns={columns}
						total={total}
						page={page}
						pageSize={pageSize}
						onPageChange={setPage}
						loading={loading}
						error={error}
					/>
				</div>
			</div>

			<ConfirmModal
				open={modalOpen}
				title={
					action === "approve"
						? "承認しますか？"
						: action === "reject"
						? "却下しますか？"
						: "paid 済みにマークしますか？"
				}
				message={target?.external_id || target?.id?.toString()}
				confirmLabel="実行"
				loading={actionLoading}
				showReason={showReason}
				requireReason={requireReason}
				onConfirm={handleConfirm}
				onCancel={() => setModalOpen(false)}
			/>
		</div>
	);
};

export default PayoutsPage;
